import React, {FC, useMemo} from 'react';

import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {useReconnectHandler} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {CellsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {usePushRooms} from '../../../../../libs/shared/ui/src/hooks/push/usePushRoom';
import {wrapInShallowSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {OfflineConversationCachingContainer} from '../../../../../srcCommon/services/offline/offlineConversationCaching';
import {useAppStateTransitionTracking} from '../../../../../srcCommon/services/performanceMetrics/appStateTransitionPerformance';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {ReselectDirectionsEnum} from '../../../../models/api/preferencesModel';
import {FetchCellsCachePoliciesEnum} from '../../../../store/panels/cells/cellsPanelActions';
import {makeGetCellsParametersRooms} from '../../../../store/panels/cells/cellsPanelSelectors';
import {
  CellsPanelPagesState,
  CellsPanelPageState,
  CellsPanelPageTypesEnum,
} from '../../../../store/panels/cells/cellsPanelState';
import {
  InboxesConversationsParameters,
  InboxesTeammateMessagesParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsAuditTrail} from './cellsAuditTrail';
import {CellsConversationsContainer} from './cellsConversationsContainer';
import {CellsItemsChildrenRenderer} from './cellsItemsBase';
import {CellsMessagesContainer} from './cellsMessagesContainer';

/*
 * Props.
 */

export type CellsContentParametersPathBuilder = (
  parameters: InboxesConversationsParameters | InboxesTeammateMessagesParameters,
) => string;
export type CellsContentChildrenRenderer = CellsItemsChildrenRenderer;

export interface CellsContentProps {
  panelId: CellsPanelId;
  pages: CellsPanelPagesState;
  cachePolicy: FetchCellsCachePoliciesEnum;
  reselectDirection: ReselectDirectionsEnum;
  buildParametersPath?: CellsContentParametersPathBuilder;
  onParametersChange: (
    parameters: InboxesConversationsParameters | InboxesTeammateMessagesParameters,
  ) => void;
  onReconnect: () => void;
  children: CellsContentChildrenRenderer;
}

/*
 * Component.
 */

export const CellsContent: FC<CellsContentProps> = (props) => {
  const {pages, onReconnect} = props;
  useAppStateTransitionTracking(pages.current);
  useReconnectHandler(onReconnect);

  const roomNames = useCellsPageRoomNames(pages.current);
  const {isReceiving} = usePushRooms(roomNames);
  const isOffline = !isReceiving;

  return (
    <>
      <OfflineConversationCachingContainer page={pages.current} />
      {renderContent(props, isOffline)}
    </>
  );
};

function renderContent(props: CellsContentProps, isOffline: boolean) {
  const {panelId, pages, cachePolicy, reselectDirection, buildParametersPath, onParametersChange, children} =
    props;
  const currentPage = pages.current;
  const hasNextPage = Boolean(pages.next);

  switch (currentPage.type) {
    case CellsPanelPageTypesEnum.MESSAGES:
      return (
        <CellsMessagesContainer
          panelId={panelId}
          hasNextPage={hasNextPage}
          page={currentPage}
          cachePolicy={cachePolicy}
          reselectDirection={reselectDirection}
          buildParametersPath={buildParametersPath}
          onParametersChange={onParametersChange}
          isOffline={isOffline}
        >
          {children}
        </CellsMessagesContainer>
      );

    // This shouldn't happen.
    case CellsPanelPageTypesEnum.AUDIT_TRAIL:
      return <CellsAuditTrail />;

    case CellsPanelPageTypesEnum.CONVERSATIONS:
      return (
        <CellsConversationsContainer
          panelId={panelId}
          hasNextPage={hasNextPage}
          page={currentPage}
          cachePolicy={cachePolicy}
          reselectDirection={reselectDirection}
          buildParametersPath={buildParametersPath}
          onParametersChange={onParametersChange}
          isOffline={isOffline}
        >
          {children}
        </CellsConversationsContainer>
      );

    default:
      throw new UnreachableError(currentPage);
  }
}

/*
 * Hooks.
 */

function useCellsPageRoomNames(page: CellsPanelPageState) {
  const delegatedTeammateId = page.cellsContext.teammateId;
  const {parameters} = page;

  const getCellsPageRoomNames = useMemo(
    () => wrapInShallowSelector(makeGetCellsParametersRooms(delegatedTeammateId, parameters)),
    [delegatedTeammateId, parameters],
  );

  return useAppSelector(getCellsPageRoomNames);
}
